"use client"

import { useEffect, useRef, useState } from "react"
import { useSession, signOut } from "next-auth/react"
import { useRouter, usePathname } from "next/navigation"
import dynamic from "next/dynamic"

const UpgradeModal = dynamic(() => import("./UpgradeModal"), { ssr: false })

// ── Plan levels ──────────────────────────────────────────
const PLAN_LEVEL: Record<string, number> = {
  lite:      0,
  essential: 1,
  pro:       2,
  internal:  3,
}

// ── Header plan badge ─────────────────────────────────────
const HEADER_PLAN: Record<string, { label: string; bg: string }> = {
  lite:      { label: "Lite",      bg: "bg-gray-600" },
  essential: { label: "Essential", bg: "bg-blue-600" },
  pro:       { label: "Pro",       bg: "bg-purple-600" },
}

function canAccess(userPlan: string, minPlan: string): boolean {
  return (PLAN_LEVEL[userPlan] ?? 0) >= (PLAN_LEVEL[minPlan] ?? 0)
}

// ── Nav items: メイングループ（常時表示） ─────────────────
const mainNavItems = [
  { icon: "📊", label: "ダッシュボード", href: "/dashboard",       minPlan: "lite" },
  { icon: "📥", label: "商品取得",       href: "/products",        minPlan: "lite" },
  { icon: "🤖", label: "AI処理",         href: "/ai-process",      minPlan: "lite" },
  { icon: "📦", label: "出品管理",       href: "/listings",        minPlan: "lite" },
  { icon: "🛒", label: "注文管理",       href: "/orders",          minPlan: "lite" },
  { icon: "✏️", label: "手動出品",       href: "/create-listing",  minPlan: "lite" },
  { icon: "📨", label: "メッセージ",     href: "/messages",        minPlan: "lite" },
  { icon: "💬", label: "サポート",       href: "/chat",            minPlan: "lite" },
  { icon: "📘", label: "マニュアル",     href: "/manual",          minPlan: "lite" },
  { icon: "🆘", label: "お問い合わせ",   href: "/support",         minPlan: "lite" },
  { icon: "💡", label: "プラン・使用量", href: "/dashboard/usage", minPlan: "lite" },
  { icon: "⚙️", label: "設定",           href: "/settings",        minPlan: "lite" },
]

// ── Nav items: サブグループ（折りたたみ可能） ─────────────
const subNavItems = [
  { icon: "💰", label: "売上レポート",    href: "/reports",    minPlan: "pro",        featureCode: "sales_report" },
  { icon: "🔍", label: "eBay検索",        href: "/search",     minPlan: "essential" },
  { icon: "🎯", label: "仕入れ情報",      href: "/sourcing",   minPlan: "essential" },
  { icon: "🤝", label: "オファー",        href: "/offers",     minPlan: "essential", featureCode: "offer_management" },
  { icon: "📈", label: "分析",            href: "/analytics",  minPlan: "pro",       featureCode: "analytics" },
  { icon: "📢", label: "広告管理",        href: "/marketing",  minPlan: "essential", featureCode: "ad_management" },
  { icon: "👁",  label: "ウォッチャー分析",href: "/watchers",   minPlan: "pro",       featureCode: "watcher_analysis" },
  { icon: "💹", label: "収益管理",        href: "/profit",     minPlan: "essential", featureCode: "profit_management" },
  { icon: "🛠️", label: "コンサル管理",    href: "/chat/admin", minPlan: "internal" },
]

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  const router   = useRouter()
  const pathname = usePathname()

  const schedulerRef    = useRef<NodeJS.Timeout | null>(null)
  const tokenRefreshRef = useRef<NodeJS.Timeout | null>(null)
  const openclawRef     = useRef<NodeJS.Timeout | null>(null)
  const notifRef        = useRef<NodeJS.Timeout | null>(null)

  const [planFeatures, setPlanFeatures]   = useState<Record<string, boolean> | null>(null)
  const [upgradeModal, setUpgradeModal]   = useState<{ label: string } | null>(null)
  const [unreadNotif, setUnreadNotif]     = useState(0)

  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      const saved  = localStorage.getItem("theme")
      const isDark = saved ? saved === "dark" : true
      document.documentElement.classList.toggle("dark", isDark)
      return isDark
    }
    return true
  })

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== "undefined") return localStorage.getItem("sidebar-collapsed") === "true"
    return false
  })

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem("sidebar-collapsed", String(next))
  }

  const [subOpen, setSubOpen] = useState(() => {
    if (typeof window !== "undefined") return localStorage.getItem("sidebar-sub-open") === "true"
    return false
  })

  const toggleTheme = () => {
    const next = !dark
    setDark(next)
    localStorage.setItem("theme", next ? "dark" : "light")
    document.documentElement.classList.toggle("dark", next)
  }

  // ── プラン機能フラグ取得（ログイン後1回だけ） ─────────
  useEffect(() => {
    if (status !== "authenticated") return
    fetch("/api/plan/features")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPlanFeatures(d) })
      .catch(() => {})
  }, [status])

  // ── Background intervals ──────────────────────────────
  useEffect(() => {
    if (schedulerRef.current) return
    schedulerRef.current = setInterval(async () => {
      try {
        const res  = await fetch("/api/scheduler", { method: "POST" })
        const data = await res.json()
        if (data.count > 0) console.log("[Scheduler] Published", data.count, "products")
      } catch {}
    }, 60000)
    return () => { if (schedulerRef.current) clearInterval(schedulerRef.current) }
  }, [])

  useEffect(() => {
    if (tokenRefreshRef.current) return
    let nextCheckMs = 120_000
    const scheduleNext = (delayMs: number) => {
      tokenRefreshRef.current = setTimeout(runRefresh, Math.max(delayMs, 30_000))
    }
    const runRefresh = async () => {
      try {
        const res  = await fetch("/api/ebay/auto-refresh", { method: "POST" })
        const data = await res.json()
        if (data.refreshed) console.log("[TokenAutoRefresh] Refreshed! Valid for", data.expiresIn, "s")
        if (data.nextCheckRecommended)                     nextCheckMs = Math.min(data.nextCheckRecommended * 1000, 300_000)
        else if (data.tokenStatus === "none")              nextCheckMs = 300_000
        else if (data.tokenStatus === "expired" || data.tokenStatus === "error") nextCheckMs = 60_000
        else                                               nextCheckMs = 120_000
      } catch { nextCheckMs = 120_000 }
      scheduleNext(nextCheckMs)
    }
    tokenRefreshRef.current = setTimeout(runRefresh, 10_000)
    return () => { if (tokenRefreshRef.current) { clearTimeout(tokenRefreshRef.current); tokenRefreshRef.current = null } }
  }, [])

  useEffect(() => {
    if (openclawRef.current) return
    const runCheck = async () => {
      try {
        const res  = await fetch("/api/openclaw", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "auto-check" }) })
        const data = await res.json()
        if (data.triggered) console.log("[OpenClaw] Auto-fetch triggered:", data.reason, data.result?.message)
      } catch {}
    }
    openclawRef.current = setTimeout(() => {
      runCheck()
      openclawRef.current = setInterval(runCheck, 10 * 60 * 1000)
    }, 30_000)
    return () => { if (openclawRef.current) { clearTimeout(openclawRef.current); clearInterval(openclawRef.current); openclawRef.current = null } }
  }, [])

  useEffect(() => {
    if (notifRef.current) return
    const fetchUnread = async () => {
      try {
        const res  = await fetch("/api/notifications?unreadOnly=true&limit=1")
        const data = await res.json()
        if (typeof data.unreadCount === "number") setUnreadNotif(data.unreadCount)
      } catch {}
    }
    fetchUnread()
    notifRef.current = setInterval(fetchUnread, 60_000)
    return () => { if (notifRef.current) { clearInterval(notifRef.current); notifRef.current = null } }
  }, [])

  // ── Early returns ──────────────────────────────────────
  if (["/login", "/", "/starter-chat"].includes(pathname)) return <>{children}</>

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="text-[13px] text-gray-400">読み込み中...</div>
      </div>
    )
  }

  if (status === "unauthenticated") {
    router.push("/login")
    return null
  }

  const userId    = (session?.user as any)?.id as string | undefined
  const isInternal = userId === "1"
  const userPlan  = session?.user?.plan || "internal"

  // 機能ロック判定: shotaは常にfalse、ロード中はfalse（楽観的）
  function isFeatureLocked(featureCode: string | undefined): boolean {
    if (!featureCode) return false
    if (isInternal)   return false
    if (!planFeatures) return false
    return planFeatures[featureCode] !== true
  }

  function handleNavClick(href: string, featureCode: string | undefined, label: string) {
    if (isFeatureLocked(featureCode)) {
      setUpgradeModal({ label })
    } else {
      router.push(href)
    }
  }

  const visibleMain = mainNavItems
  const visibleSub  = subNavItems.filter(item => item.minPlan !== "internal" || isInternal)

  // /manual/starter 配下ではツールサイドバーを非表示（consulting_starter 専用エリア）
  const hideToolSidebar = pathname.startsWith("/manual/starter")

  return (
    <div className={`min-h-screen text-[13px] ${dark ? "bg-gray-950 text-white" : "bg-gray-50 text-gray-900"}`}>
      {/* Header */}
      <header className={`border-b px-4 py-2.5 ${dark ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-white"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => router.push("/dashboard")}>
            <span className="text-lg">⚡</span>
            <span className="text-[14px] font-bold text-blue-500">eBay AutoPilot</span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[12px] ${dark ? "text-gray-400" : "text-gray-600"}`}>{session?.user?.name}</span>
            {HEADER_PLAN[userPlan] && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${HEADER_PLAN[userPlan].bg} text-white`}>
                {HEADER_PLAN[userPlan].label}
              </span>
            )}
            <button
              onClick={() => { setUnreadNotif(0); router.push("/notifications") }}
              className={`relative rounded-full w-8 h-8 flex items-center justify-center text-[14px] transition-colors ${dark ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-100 hover:bg-gray-200"}`}
              title="通知"
            >
              🔔
              {unreadNotif > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                  {unreadNotif > 99 ? "99+" : unreadNotif}
                </span>
              )}
            </button>
            <button
              onClick={toggleTheme}
              className={`rounded-full w-8 h-8 flex items-center justify-center text-[14px] transition-colors ${dark ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-100 hover:bg-gray-200"}`}
              title={dark ? "ライトモードへ" : "ダークモードへ"}
            >
              {dark ? "☀️" : "🌙"}
            </button>
            <button
              onClick={() => router.push("/settings")}
              className={`rounded-full w-8 h-8 flex items-center justify-center text-[14px] transition-colors ${dark ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-100 hover:bg-gray-200"}`}
              title="設定"
            >
              ⚙️
            </button>
            <button
              onClick={() => signOut({ callbackUrl: "/login" })}
              className={`rounded px-2.5 py-1 text-[11px] border ${dark ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border-gray-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300"}`}
            >
              ログアウト
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        {!hideToolSidebar && <nav className={`${collapsed ? "w-12" : "w-48"} min-h-screen border-r flex flex-col transition-all duration-200 ${dark ? "border-gray-800 bg-gray-900" : "border-gray-200 bg-white"}`}>
          {/* トグルボタン */}
          <div className={`flex items-center ${collapsed ? "justify-center" : "justify-end"} px-2 py-1.5 border-b ${dark ? "border-gray-800" : "border-gray-200"}`}>
            <button
              onClick={toggleCollapsed}
              className={`w-7 h-7 flex items-center justify-center rounded text-[13px] transition-colors ${dark ? "text-gray-400 hover:bg-gray-800 hover:text-gray-200" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
              title={collapsed ? "サイドバーを展開" : "サイドバーを折りたたむ"}
            >
              {collapsed ? "▶" : "◀"}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-1.5">
            {/* メイングループ */}
            <div className="space-y-0.5">
              {visibleMain.map((item) => {
                const locked = isFeatureLocked((item as any).featureCode)
                return (
                  <div
                    key={item.href}
                    onClick={() => handleNavClick(item.href, (item as any).featureCode, item.label)}
                    className={`group relative flex items-center ${collapsed ? "justify-center" : "gap-2"} rounded ${collapsed ? "px-0 py-1.5" : "px-2.5 py-1.5"} text-[13px] cursor-pointer ${
                      pathname.startsWith(item.href)
                        ? "bg-blue-600/20 text-blue-600"
                        : locked
                          ? dark
                            ? "text-gray-600 hover:bg-gray-800 hover:text-gray-500"
                            : "text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                          : dark
                            ? "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <span className="text-sm flex-shrink-0">{item.icon}</span>
                    {!collapsed && (
                      <span className="truncate flex-1">{item.label}</span>
                    )}
                    {!collapsed && locked && (
                      <span className="text-[10px] text-gray-600 flex-shrink-0">🔒</span>
                    )}
                    {collapsed && (
                      <span className={`absolute left-full ml-2 px-2 py-1 rounded text-[12px] whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 ${dark ? "bg-gray-800 text-gray-200 shadow-lg" : "bg-gray-700 text-white shadow-lg"}`}>
                        {item.label}{locked ? " 🔒" : ""}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>

            {/* 区切り線 + その他トグル */}
            {visibleSub.length > 0 && (
              <>
                <div className={`my-1.5 border-t ${dark ? "border-gray-800" : "border-gray-200"}`} />
                <button
                  onClick={() => setSubOpen(prev => { const next = !prev; localStorage.setItem("sidebar-sub-open", String(next)); return next })}
                  className={`relative z-10 w-full flex items-center ${collapsed ? "justify-center" : "gap-2 px-2.5"} py-1.5 rounded text-[12px] cursor-pointer transition-colors ${dark ? "text-gray-500 hover:text-gray-300 hover:bg-gray-800" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                >
                  {collapsed ? (
                    <span className="text-[11px]">...</span>
                  ) : (
                    <>
                      <span>その他</span>
                      <span className="text-[9px]">{subOpen ? "▲" : "▼"}</span>
                    </>
                  )}
                </button>

                {subOpen && (
                  <div className="space-y-0.5 mt-0.5">
                    {visibleSub.map((item) => {
                      const locked = isFeatureLocked(item.featureCode)
                      return (
                        <div
                          key={item.href}
                          onClick={() => handleNavClick(item.href, item.featureCode, item.label)}
                          className={`group relative flex items-center ${collapsed ? "justify-center" : "gap-2"} rounded ${collapsed ? "px-0 py-1.5" : "px-2.5 py-1.5"} text-[13px] cursor-pointer ${
                            pathname.startsWith(item.href)
                              ? "bg-blue-600/20 text-blue-600"
                              : locked
                                ? dark
                                  ? "text-gray-600 hover:bg-gray-800 hover:text-gray-500"
                                  : "text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                                : dark
                                  ? "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                        >
                          <span className="text-sm flex-shrink-0">{item.icon}</span>
                          {!collapsed && (
                            <span className="truncate flex-1">{item.label}</span>
                          )}
                          {!collapsed && locked && (
                            <span className="text-[10px] text-gray-600 flex-shrink-0">🔒</span>
                          )}
                          {collapsed && (
                            <span className={`absolute left-full ml-2 px-2 py-1 rounded text-[12px] whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 ${dark ? "bg-gray-800 text-gray-200 shadow-lg" : "bg-gray-700 text-white shadow-lg"}`}>
                              {item.label}{locked ? " 🔒" : ""}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )}

          </div>
        </nav>}

        <main className="flex-1 p-4 max-w-[1400px]">{children}</main>
      </div>

      {/* アップグレードモーダル */}
      {upgradeModal && (
        <UpgradeModal
          featureLabel={upgradeModal.label}
          onClose={() => setUpgradeModal(null)}
        />
      )}
    </div>
  )
}
